import { useEffect, useMemo, useRef, useState } from "react";

import useStateHandler from "@/hooks/useStateHandler";
import ContinueButton from "@/shared-components/buttons/components/ContinueButton";
import BackArrowIcon from "@/shared-components/icons/components/public/BackArrowIcon";
import XIcon from "@/shared-components/icons/components/public/XIcon";
import GenericPopup from "@/shared-components/popups/components/GenericPopup";

import {
  TEMPLATE_DESIGNS,
  TEMPLATE_FORMATS,
} from "../constants";
import renderTemplateToBlob from "../functions/renderTemplateToBlob";

import TemplatePreview from "./TemplatePreview";

import "./styles/PopupTemplatePreview.css";

const PopupTemplatePreview = ({
  open,
  onClose,
  onGoBack,
  format,
  mode,
  design,
  croppedUrl,
  originalImage,
  croppedFile,
  templatePreviewRef,
  isDownloading,
  setIsDownloading,
  artworkData,
  onShowTip,
  onDownloadCompleted,
}) => {
  const { stateHandler } = useStateHandler();

  const stepNumber = design === TEMPLATE_DESIGNS.TYPE_ONE ? 4 : 3;
  const safeFormat = format || TEMPLATE_FORMATS.STORY;
  const safeMode = mode || "dark";

  const canvasRef = useRef(null);
  const [previewScale, setPreviewScale] = useState(0.3);

  const previewDimensions = useMemo(() => {
    const isPost = safeFormat === TEMPLATE_FORMATS.POST;
    return {
      width: 1080,
      height: isPost ? 1350 : 1920,
    };
  }, [safeFormat]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const canvasElement = canvasRef.current;
    if (!canvasElement) {
      return undefined;
    }

    const updateScale = () => {
      const availableWidth = canvasElement.clientWidth;
      const availableHeight = canvasElement.clientHeight;

      if (!availableWidth || !availableHeight) {
        return;
      }

      const widthRatio = availableWidth / previewDimensions.width;
      const heightRatio = availableHeight / previewDimensions.height;
      const nextScale = Math.min(widthRatio, heightRatio, 1);

      setPreviewScale((currentScale) =>
        Math.abs(currentScale - nextScale) > 0.005 ? nextScale : currentScale
      );
    };

    const observerIsAvailable =
      typeof window !== "undefined" &&
      typeof window.ResizeObserver !== "undefined";
    const resizeObserver = observerIsAvailable
      ? new window.ResizeObserver(() => {
        updateScale();
      })
      : null;

    resizeObserver?.observe(canvasElement);
    window.addEventListener("resize", updateScale);

    updateScale();

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateScale);
    };
  }, [open, previewDimensions.height, previewDimensions.width]);

  const handleDownload = async () => {
    if (!artworkData || !design || !safeFormat) {
      return;
    }

    let temporaryCroppedUrl;

    try {
      setIsDownloading(true);

      const artworkAbout = artworkData?.artwork_about ?? {};
      const artist = artworkAbout.artwork_gallery_artist ?? {};

      const artistName = artist.artist_name || "Artist Name";
      const artworkTitle = artworkAbout.artwork_title || "Artwork Title";
      const sanitizedTitle = artworkTitle
        .trim()
        .replace(/[\\/:*?"<>|]+/g, "")
        .replace(/\s+/g, "-") || "template";

      const preferredImage = (() => {
        if (design !== TEMPLATE_DESIGNS.TYPE_ONE) {
          return originalImage;
        }

        if (croppedUrl) {
          return croppedUrl;
        }

        if (croppedFile instanceof File) {
          temporaryCroppedUrl = URL.createObjectURL(croppedFile);
          return temporaryCroppedUrl;
        }

        return originalImage;
      })();

      const blob = await renderTemplateToBlob({
        design,
        format: safeFormat,
        mode: safeMode,
        artistName,
        artworkTitle,
        preferredImage,
        fallbackImage: originalImage || croppedUrl,
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${sanitizedTitle}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      stateHandler.set("temporalPopup", {
        text: "Template downloaded successfully",
        type: "navbar-present",
      });

      onDownloadCompleted?.();
    } catch (error) {
      console.error("Template download failed", error);
      stateHandler.set("temporalPopup", {
        text: "We could not generate the template. Please try again.",
        style: "bg-red-500",
      });
    } finally {
      if (temporaryCroppedUrl) {
        URL.revokeObjectURL(temporaryCroppedUrl);
      }
      setIsDownloading(false);
    }
  };

  const tipDisabled = !onShowTip;

  const handleGoBack = onGoBack || onClose;

  return (
    <GenericPopup
      open={open}
      className="artwork-options__download-template-container preview-step__container"
      opacity
      onTouchStart={(event) => event.stopPropagation()}
      onTouchEnd={(event) => event.stopPropagation()}
      onWheel={(event) => event.stopPropagation()}
    >
      <div className="preview-step__header">
        <BackArrowIcon className="preview-step__back" onClick={handleGoBack} />
        <p className="preview-step__title">Generate template</p>
        <p className="preview-step__subtitle">
          <span>{stepNumber}.</span>
          Preview and download
        </p>
        <XIcon className="preview-step__close" onClick={onClose} />
      </div>

      <div className="preview-step__canvas" ref={canvasRef}>
        <div
          className="preview-step__canvas-content"
          style={{
            width: `${previewDimensions.width}px`,
            height: `${previewDimensions.height}px`,
            transform: `scale(${previewScale})`,
            marginBottom: `-${previewDimensions.height * (1 - previewScale)}px`,
            marginLeft: `-${(previewDimensions.width * (1 - previewScale)) / 2}px`,
            marginRight: `-${(previewDimensions.width * (1 - previewScale)) / 2}px`,
          }}
        >
          <TemplatePreview
            reference={templatePreviewRef}
            croppedUrl={croppedUrl}
            design={design}
            format={safeFormat}
            mode={safeMode}
            originalImage={originalImage}
            artworkData={artworkData}
          />
        </div>
      </div>

      <button
        type="button"
        className="preview-step__tip"
        onClick={onShowTip}
        disabled={tipDisabled}
      >
        For better engagement check Marina’s tip
      </button>

      <ContinueButton
        className="preview-step__cta"
        onClick={handleDownload}
        loading={isDownloading}
      >
        Download
      </ContinueButton>
    </GenericPopup>
  );
};

export default PopupTemplatePreview;
